// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import isNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getAllBrandsDataView, getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import translate from "@insite/client-framework/Translate";
import { BrandModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import BrandAlphabetNavigation, {
    BrandAlphabetNavigationStyles,
} from "@insite/content-library/Widgets/Brand/BrandAlphabetNavigation";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import ShareViaEmail from "@mcmc/App/Components/Common/ShareViaEmail";
import shareBrandViaEmail from "@mcmc/App/Handlers/Brands/ShareBrandsViaEmail";
import setBreadcrumbActions from "@mcmc/App/Handlers/Layout/setBreadcrumbActions";
import { initializeScrollTo } from "@mcmc/App/Libraries";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import {
    getBrandCategories,
    getBrandCategoriesLoadingState,
    getSelectedBrandViewType,
    getSelectedSolutionParameters,
} from "@mcmc/App/Store/Selectors/CustomBrandSelectors";
import { totalSectionGlobalStyles } from "@mcmc/App/WidgetExtensions/AccountPagesGlobalStyle";
import { categoryList as brandListStyles } from "@mcmc/Overrides/Widgets/Category/CategoryList.style";
import StyledBox from "@mcmc/XDL/Components/Common/Box";
import CloudinaryImage from "@mcmc/XDL/Components/Common/CloudinaryImage";
import FancyScroll from "@mcmc/XDL/Components/Common/FancyScroll";
import Notify from "@mcmc/XDL/Components/Notify";
import { screen } from "@mcmc/XDL/Helpers";
import Search from "@mcmc/XDL/Icons/Search";
import { ellipsis, hideScrollBar, Roboto } from "@mcmc/XDL/Utilities";
import { transformationsMap } from "@mcmc/XDL/Utilities/cloudinary";
import { printIconStyle } from "@mcmc/XDL/Utilities/sharedStyles";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: McMcApplicationState) => {
    const selectedSolutionParameters = getSelectedSolutionParameters(state);
    const solutionValue = selectedSolutionParameters?.solution;
    const brandsDataView = getBrandsDataView(state, selectedSolutionParameters || {});
    const allBrandsDataView = solutionValue ? brandsDataView : getAllBrandsDataView(state);
    const shouldLoadBrands = solutionValue && !brandsDataView.isLoading && !brandsDataView.value;

    return {
        allBrandsDataView,
        /* No need for it, we need to disable the alphabet during search
        brandAlphabetState: state.pages.brands.brandAlphabetState,
        */
        selectedBrandViewType: getSelectedBrandViewType(state),
        brandCategories: getBrandCategories(state),
        isBrandCategoriesLoading: getBrandCategoriesLoadingState(state),
        selectedSolutionParameters,
        shouldLoadBrands,
    };
};

const mapDispatchToProps = {
    setBreadcrumbActions,
    shareBrandViaEmail,
    loadBrands,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

type BrandLetterMap = {
    [letter: string]: BrandModel[];
};

export interface BrandListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: InjectableCss;
    searchStyledBox?: InjectableCss;
    searchWrapper?: InjectableCss;
    brandAlphabetNavigation?: BrandAlphabetNavigationStyles;
    searchBrandInput?: TextFieldProps;
    content?: InjectableCss;
    brandsHeader?: TypographyPresentationProps;
    brandsDetailsSection?: InjectableCss;
    brandsDetailsTopSection?: InjectableCss;
    brandsLetter?: TypographyPresentationProps;
    backToTopLink?: LinkPresentationProps;
    brandCategoryWrapper?: InjectableCss;
    brandCategoryContent?: InjectableCss;
    categoryLink?: LinkPresentationProps;
    brandLink?: LinkPresentationProps;
    brandImageWrapper?: InjectableCss;
    brandContentWrapper?: InjectableCss;
    styledBrandBox?: InjectableCss;
}

export const listStyles: BrandListStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    searchStyledBox: {
        css: css`
            @media print {
                display: none;
            }
        `,
    },
    searchWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin: -15px;
            ${screen("lte", "xs")`
                flex-direction: column;
            `}
        `,
    },
    searchBrandInput: {
        cssOverrides: {
            formField: css`
                ${getProp("theme.formField.defaultProps.cssOverrides.formField")}
                flex: 0 0 280px;
                padding: 15px;
                ${screen("lte", "sm")`
                    flex: 0 0 50%;
                `}
            `,
            inputSelect: css`
                ${getProp("theme.formField.defaultProps.cssOverrides.inputSelect")}
                padding: 0 10px 0 33px !important;
            `,
        },
        iconProps: {
            src: Search,
            css: css`
                color: ${getColor("primary")};
                left: 2px;
            `,
        },
    },
    content: {
        css: css`
            padding-top: 20px;
            span[direction="up"] {
                top: 0;
                &:before {
                    height: 0;
                }
            }
            @media print {
                span[direction="down"],
                span[direction="up"] {
                    display: none;
                }
            }
        `,
    },
    brandsHeader: {
        variant: "h2",
        css: css`
            padding-bottom: 20px;
            border-bottom: 1px solid ${getColor("common.border")};
            margin-bottom: 20px;
        `,
    },
    brandsDetailsSection: {
        css: css`
            padding-bottom: 40px;
            margin-bottom: 20px;
            border-bottom: 1px solid ${getColor("common.border")};

            @media print {
                width: 100%;
                page-break-inside: avoid;

                &:after {
                    content: "";
                    clear: both;
                    display: table;
                }
            }
        `,
    },
    brandsDetailsTopSection: {
        css: css`
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            margin-bottom: 15px;
        `,
    },
    brandsLetter: {
        variant: "h3",
        weight: "bold",
        css: css`
            margin-bottom: 0;
        `,
    },
    backToTopLink: {
        color: "secondary",
        css: css`
            ${getProp("theme.link.defaultProps.css")}
            @media print {
                display: none;
            }
        `,
    },
    brandCategoryWrapper: {
        css: css`
            ${brandListStyles.categoriesContentWrapper?.css}
            @media print {
                display: block;
            }
        `,
    },
    brandCategoryContent: {
        css: css`
            ${hideScrollBar}
            margin: 0;
            height: 230px;
            overflow: auto;
            display: flex;
            flex-direction: column;
            @media print {
                box-shadow: none;
                background: none;
                border: 0;
                page-break-inside: avoid;
                height: auto;
                flex-direction: row;
                flex-wrap: wrap;
                padding: 20px 0;
            }
        `,
    },
    categoryLink: {
        color: "text",
        hoverStyle: {
            color: "secondary",
        },
        typographyProps: {
            size: 16,
            lineHeight: "18px",
            css: css`
                min-height: 36px;
                ${ellipsis(2, 18)}
            `,
        },
        css: css`
            ${getProp("theme.link.defaultProps.css")}
            margin-bottom: 15px;

            @media print {
                flex: 1 1 100%;
            }
        `,
    },
    brandLink: {
        color: "text",
        hoverStyle: {
            textDecoration: "underline",
            color: "text",
        },
        typographyProps: {
            ...Roboto,
            weight: 400,
        },
        css: css`
            ${getProp("theme.link.defaultProps.css")}
            display: inline-block;
            align-self: flex-start;
            margin-bottom: 8px;
            &:last-child {
                margin-bottom: 0;
            }
            @media print {
                margin: 0 20px 10px 0;
            }
        `,
    },

    brandImageWrapper: {
        css: css`
            ${brandListStyles.categoryImageWrapper?.css}
            @media print {
                display: none;
            }
        `,
    },
    brandContentWrapper: {
        css: css`
            ${brandListStyles.categoryImageWrapper?.css}
            @media print {
                display: none;
            }
        `,
    },
    styledBrandBox: {
        css: css`
            ${brandListStyles.styledBox?.css}
            @media print {
                box-shadow: none;
                background: none;
                border: 0;
                page-break-inside: avoid;
                display: inline-block;
                width: calc(100% / 3);
            }
        `,
    },
};

const styles = listStyles;

/* No need for this State, I changed the structue of the data to match our customization
interface BrandListState {
    expanded: boolean | "mixed"; 
    brandLetterDetails: BrandAlphabetLetterModel[];
    brandSections: BrandSection[];
} */

/**
 * This will display all Brands grouped by the first character.
 * Starting with numbers grouped into '#'.
 * @param props
 */
const BrandList: FC<Props> = (props: Props) => {
    const {
        allBrandsDataView,
        selectedBrandViewType,
        brandCategories,
        isBrandCategoriesLoading,
        setBreadcrumbActions,
        shareBrandViaEmail,
        selectedSolutionParameters,
        loadBrands,
        shouldLoadBrands,
    } = props;
    const alphabeticalOrder = selectedBrandViewType === "alphabeticalSelect";

    React.useEffect(() => {
        if (shouldLoadBrands) {
            loadBrands(selectedSolutionParameters || {});
        }
    }, [shouldLoadBrands, selectedSolutionParameters?.solution]);

    React.useEffect(() => {
        setBreadcrumbActions({
            breadcrumbActions: (
                <StyledWrapper {...totalSectionGlobalStyles.breadcrumbActionsWrapper}>
                    {alphabeticalOrder && (
                        <ShareViaEmail
                            shareAction={shareBrandViaEmail}
                            additionalApiParameter={selectedSolutionParameters}
                            shareModalTitle={translate("Share Line Cards")}
                        />
                    )}
                    <Link {...printIconStyle} onClick={openPrintDialog} aria-label={translate("Print Brand List")}>
                        <VisuallyHidden>{translate("Print")}</VisuallyHidden>
                    </Link>
                </StyledWrapper>
            ),
        });
        return () => {
            setBreadcrumbActions({
                breadcrumbActions: null,
            });
        };
    }, [alphabeticalOrder]);

    const [searchTerm, setSearchTerm] = React.useState("");

    const getFilteredBrandList = React.useCallback(
        (brandList: any) => {
            const filteredBrandList = brandList.filter((brand: any) =>
                brand.name.toLowerCase().includes(searchTerm.toLowerCase()),
            );
            return filteredBrandList;
        },
        [searchTerm],
    );

    const brandList = allBrandsDataView.value || [];
    const filteredBrandList = React.useMemo(() => getFilteredBrandList(brandList), [brandList, getFilteredBrandList]);

    const { letters, brandLettersMap } = buildLettersAndMap(filteredBrandList);
    const brandLetterDetails = getBrandAlphabet(brandLettersMap) || [];
    const { brandSections } = buildBrandSections(letters, brandLettersMap);

    const [lastLetterClicked, setlastLetterClicked] = React.useState("");
    React.useLayoutEffect(() => {
        if (lastLetterClicked) {
            const alphabetElement = document.getElementById("alphabetNavigation");
            const letterElement = document.getElementById(`letter-${lastLetterClicked}`);
            if (alphabetElement && letterElement) {
                const alphabetOffset = alphabetElement.offsetTop;
                const letterOffset = letterElement.offsetTop;
                if (letterOffset - alphabetOffset > 0) {
                    window.scrollTo(0, letterOffset - alphabetOffset);
                }
            }
            setlastLetterClicked("");
        }
    }, [lastLetterClicked]);

    if (allBrandsDataView.isLoading || isBrandCategoriesLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (brandList.length === 0 || brandCategories?.categoriesCollection.length === 0) {
        return <Notify messageType="danger" hideCloseButton body={translate("No Brands.")} />;
    }

    const handleBackToTop = () => {
        initializeScrollTo("#brand-navigation");
    };

    /* -- No need for this function on our design
    const onClickBrandSection = (section: BrandSection) => () => {};
    */

    /* -- We don't need the collapse and expand because we don't have an accordions on our design
    const onCollapse = (event: React.MouseEvent) => {
    }; */

    /* -- We don't need the collapse and expand because we don't have an accordions on our design
    const onExpand = (event: React.MouseEvent) => {
    }; */

    const handleBrandLetterClick = (letter: string) => {
        // const section = state.brandSections.find(item => item.letter === letter);
        // section && onClickBrandSection(section)();
        setlastLetterClicked(letter || "");
    };

    const DisclamerMessage = (
        <Notify messageType="info" hideCloseButton body={siteMessage("MCMC_Brand_Disclaimer_Message")} />
    );

    return (
        <StyledWrapper {...styles.container} id="brand-navigation" data-test-selector="brandList">
            {alphabeticalOrder ? (
                <>
                    <StyledBox {...styles.searchStyledBox}>
                        <StyledWrapper {...styles.searchWrapper}>
                            <BrandAlphabetNavigation
                                letterDetails={brandLetterDetails}
                                extendedStyles={styles.brandAlphabetNavigation}
                                onBrandLetterClick={handleBrandLetterClick}
                            />
                            <TextField
                                placeholder={translate("Type a Manufacturer Name")}
                                value={searchTerm}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    setSearchTerm(event.target.value)
                                }
                                data-test-selector="brandList_search"
                                {...styles.searchBrandInput}
                            />
                        </StyledWrapper>
                    </StyledBox>
                    <StyledWrapper {...styles.content}>
                        <Typography {...styles.brandsHeader}>{translate("All Manufacturers")}</Typography>
                        {DisclamerMessage}
                        {brandSections?.length > 0 ? (
                            <>
                                {brandSections.map(brandDetails => (
                                    <StyledWrapper
                                        key={brandDetails.letter}
                                        id={`letter-${brandDetails.letter === "#" ? "0-9" : brandDetails.letter}`}
                                        data-test-selector="brandSection"
                                        {...styles.brandsDetailsSection}
                                    >
                                        <StyledWrapper {...styles.brandsDetailsTopSection}>
                                            <Typography {...styles.brandsLetter}>{brandDetails.letter}</Typography>
                                            <Link {...styles.backToTopLink} onClick={handleBackToTop}>
                                                {translate("Back to Top")}
                                            </Link>
                                        </StyledWrapper>
                                        <StyledWrapper {...brandListStyles.categoriesContentWrapper}>
                                            {brandDetails.brandMap.map(brand => (
                                                <StyledBox {...styles.styledBrandBox} key={brand.detailPagePath}>
                                                    <Link
                                                        href={brand.detailPagePath}
                                                        {...brandListStyles.link}
                                                        aria-label={brand.name}
                                                    >
                                                        <StyledWrapper {...styles.brandImageWrapper}>
                                                            <CloudinaryImage
                                                                imageTransformations={transformationsMap.product_sm}
                                                                src={brand.logoSmallImagePath}
                                                                altText={brand.logoAltText}
                                                            />
                                                        </StyledWrapper>
                                                        <StyledWrapper {...brandListStyles.categoryNameWrapper}>
                                                            <Typography {...brandListStyles.categoryName}>
                                                                {brand.name}
                                                            </Typography>
                                                        </StyledWrapper>
                                                    </Link>
                                                </StyledBox>
                                            ))}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ))}
                            </>
                        ) : (
                            <Notify messageType="danger" hideCloseButton body={translate("No Brands Found.")}></Notify>
                        )}
                    </StyledWrapper>
                </>
            ) : (
                <>
                    {brandCategories && (
                        <StyledWrapper {...styles.content}>
                            {DisclamerMessage}
                            <StyledWrapper {...styles.brandCategoryWrapper}>
                                {brandCategories?.categoriesCollection.length > 0 &&
                                    brandCategories.categoriesCollection.map((category, index) => {
                                        const parentBox = React.createRef<HTMLDivElement>();
                                        return (
                                            // eslint-disable-next-line react/no-array-index-key
                                            <FancyScroll refElement={parentBox} key={index}>
                                                <StyledBox ref={parentBox} {...styles.brandCategoryContent}>
                                                    <Link href={category.url} {...styles.categoryLink}>
                                                        {category.name}
                                                    </Link>
                                                    {category.brands?.length > 0 &&
                                                        category.brands.map(brand => (
                                                            <Link
                                                                key={brand.id}
                                                                href={brand.detailPagePath}
                                                                {...styles.brandLink}
                                                            >
                                                                {brand.name}
                                                            </Link>
                                                        ))}
                                                </StyledBox>
                                            </FancyScroll>
                                        );
                                    })}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                </>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BrandList),
    definition: {
        group: "Brands",
        icon: "List",
        allowedContexts: ["BrandsPage"],
    },
};

export default widgetModule;

interface BrandSection {
    letter: string;
    brandMap: BrandModel[];
    // expanded: boolean;
}

/**
 * Takes in a brand list and sorts it into #, A-Z, Other characters.
 *
 * @param brandList The list of brands that should be sorted.
 */
const buildLettersAndMap = (brandList: BrandModel[]): { letters: string[]; brandLettersMap: BrandLetterMap } => {
    let letters: string[] = [];
    let letter: string;
    let newLetters: string[] = letters;
    const brandLettersMap: BrandLetterMap = {};
    for (let i = 0; i < brandList.length; i = i + 1) {
        const brand = brandList[i];
        letter = brand.name[0] ? brand.name[0].toLowerCase() : "";
        if (isNumeric(letter)) {
            letter = "#";
        }
        if (!brandLettersMap[letter]) {
            brandLettersMap[letter] = [];
        }
        brandLettersMap[letter].push(brand);
        newLetters = [...newLetters, letter];
    }
    letters = newLetters.filter((letter, index, array) => array.indexOf(letter) === index);
    if (brandLettersMap["#"]) {
        brandLettersMap["#"].sort((a, b) => parseInt(a.name, 10) - parseInt(b.name, 10));
    }

    return { letters, brandLettersMap };
};

/**
 * Take in the props and create easier to work with state objects.
 *
 * @param letters List of letters and special characters.
 * @param brandLettersMap Brand list sorted into letter lists.
 * @param brandAlphabet Letter count list.
 * @param expanded The expanded value to use if not undefined.
 */
const buildBrandSections = (
    letters: string[],
    brandLettersMap: BrandLetterMap,
    // brandAlphabet: BrandAlphabetLetterModel[],
    // expanded: boolean | undefined,
): {
    // I used another funtion to get the brandLetterDetails
    // brandLetterDetails: {
    //     count: number;
    //     letter: string;
    //     linkable: boolean;
    // }[];
    brandSections: {
        letter: string;
        brandMap: BrandModel[];
        // expanded: boolean;
    }[];
} => {
    // const brandLetterDetails = brandAlphabet.map(alphabetItem => ({
    //     ...alphabetItem,
    //     linkable: alphabetItem.count > 0,
    // }));
    const brandSections = letters.map(letter => ({
        letter,
        brandMap: brandLettersMap[letter],
        // expanded: expanded || false,
    }));

    return { brandSections };
};

// This function to add clickable to the exist letters to get the correct value during the search
const getBrandAlphabet = (brandLettersMap: BrandLetterMap) => {
    const defaultAlphabet: string[] = "#abcdefghijklmnopqrstuvwxyz".split("");
    return defaultAlphabet.map(alphabet => {
        let linkable = false;
        if (brandLettersMap[alphabet]) {
            linkable = true;
        }
        return {
            letter: alphabet,
            linkable,
        };
    });
};
